import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

import { CognitoUserSession } from 'amazon-cognito-identity-js';
import { useIntl } from 'react-intl';

import { ISocialLoginInput, ProviderType } from 'generated/graphql-gateway';
import { ICommunicationPreference, IFavoriteStore } from 'generated/rbi-graphql';
import { useNavigation } from 'hooks/navigation/use-navigation';
import useErrorModal from 'hooks/use-error-modal';
import { getCurrentSession } from 'remote/auth';
import { ModalAuthScreen, ModalAuthState, UpdateUserInfoOptions } from 'state/auth/types';
import { useLocale } from 'state/intl';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { Region } from 'utils/environment/types';
import { ISOsToRegions } from 'utils/form/constants';
import LocalStorage, { StorageKeys } from 'utils/local-storage';
import { routes } from 'utils/routing';
import { useMemoAll } from 'utils/use-memo-all';

import {
  ISignIn,
  ISignUpResult,
  useAccountAuthentication,
} from './hooks/use-account-authentication';
import { UserDetails, useCurrentUser } from './hooks/use-current-user';
import { ModalAuthHook, useModalAuth } from './hooks/use-modal-auth';

export type UserInfo = {
  dob: string;
  name: string;
  phoneNumber: string;
  promotionalEmails: boolean;
  isoCountryCode: string;
  zipcode: string;
  defaultReloadAmt: number;
  defaultCheckoutPaymentMethodId?: string;
  defaultReloadPaymentMethodId?: string;
  autoReloadEnabled: boolean;
  autoReloadThreshold: number;
  adsPersonalizationOptOut?: boolean;
};

type SignupArgs = {
  email: string;
  name: string;
  phoneNumber: string;
  country: string;
  wantsPromotionalEmails: boolean;
  crmAttributes?: { source: string };
};

export interface AuthInterface {
  // Computed values
  loading: boolean;
  originLocation: null | string;
  user: null | UserDetails;
  otpAuthError: null | string;
  modalAuthState: ModalAuthState;
  modalAuthIsOpen: boolean;
  // Functions
  getCurrentSession(): Promise<null | CognitoUserSession>;
  refetchCurrentUser(): Promise<void>;
  refreshCurrentUser(): Promise<void>;
  refreshCurrentUserWithNewSession(): Promise<void>;
  isAuthenticated: boolean;
  setOriginLocation(location: null | string): void;
  signIn(args: ISignIn): Promise<void>;
  /**
   * Signs out the user.
   * @param isGlobalSignOut If `true` the user will be sign out from all devices.
   */
  signOut(isGlobalSignOut?: boolean): Promise<void>;
  signUp(
    args: SignupArgs,
    signInOverride?: (args: ISignIn) => Promise<void>,
    authenticationMethod?: string,
    socialAuthenticationType?: ProviderType
  ): Promise<ISignUpResult>;
  socialLogin(args: ISocialLoginInput): Promise<void>;
  updateUserInfo(args: UserInfo, options?: UpdateUserInfoOptions): Promise<void>;
  updateUserCommPrefs(
    commPrefs: Array<ICommunicationPreference>,
    options?: {
      shouldThrowException?: boolean;
    }
  ): Promise<void>;
  updateUserFavStores(favStores: Array<IFavoriteStore>): Promise<void>;
  useUpdateMeMutationLoading: boolean;
  validateLogin(args: { jwt: string; username: string }): Promise<void>;
  validateLoginOtp(args: { otpCode: string }): Promise<void>;
  setOtpAuthError(error: null | string): void;
  setModalAuthState: ModalAuthHook['setModalAuthState'];
  /**
   * Used to determine if an already logged-in user is currently re-authenticating
   * and avoid navigating away from auth-guarded components.
   * This is useful in certain data-sensitive operations to make sure that the
   * user performing the action is the real owner of the account. e.g., editing
   * user account information.
   */
  isReAuthenticating: boolean;
  setIsReAuthenticating: (value: boolean) => void;
}

export const AuthContext = createContext<AuthInterface>(({} as any) as AuthInterface);
export const useAuthContext = () => useContext(AuthContext);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const { navigate } = useNavigation();
  const { region } = useLocale();
  const { formatMessage } = useIntl();
  const { setModalAuthState, modalAuthIsOpen, modalAuthState } = useModalAuth();

  const enableAccountRegionVerification = useFlag(
    LaunchDarklyFlag.ENABLE_ACCOUNT_REGION_VERIFICATION
  );

  const [otpAuthError, setOtpAuthError] = useState(null);

  const onConfirmErrorDialog = useCallback(() => {
    // If the user is in the modal auth flow, we want to bypass the redirection to the sign up page and instead show the sign up form in the auth modal
    if (modalAuthIsOpen) {
      setModalAuthState({ screen: ModalAuthScreen.SIGN_IN });
      return;
    }
    navigate(routes.signUp, {
      state: { activeRouteIsSignIn: true },
      replace: true,
    });
  }, [modalAuthIsOpen, navigate, setModalAuthState]);

  const [ErrorDialog, openErrorDialog] = useErrorModal({
    onConfirm: onConfirmErrorDialog,
    modalAppearanceEventMessage: 'Error: Auth Error',
  });

  const [isReAuthenticating, setIsReAuthenticating] = useState(false);

  const {
    refetchCurrentUser,
    refreshCurrentUser,
    refreshCurrentUserWithNewSession,
    setCurrentUser,
    updateUserCommPrefs,
    updateUserFavStores,
    updateUserInfo,
    user,
    userLoading,
    useUpdateMeMutationLoading,
    isAuthenticated,
  } = useCurrentUser({
    openErrorDialog,
  });

  const {
    authLoading,
    originLocation,
    setOriginLoc,
    signIn,
    signOut,
    signUp,
    socialLogin,
    validateLogin,
    validateLoginOtp,
  } = useAccountAuthentication({
    refreshCurrentUser,
    openErrorDialog,
    setCurrentUser,
    setModalAuthState,
    modalAuthIsOpen,
  });

  useEffect(() => {
    // save redirect url so user can get redirected correctly after login
    LocalStorage.setItem(StorageKeys.AUTH_REDIRECT, { callbackUrl: originLocation });
  }, [originLocation]);

  useEffect(() => {
    if (enableAccountRegionVerification && user?.details?.isoCountryCode) {
      const userRegion = (ISOsToRegions[user.details.isoCountryCode] as unknown) as Region;

      // Make sure we have values for the current region and user region
      if (region && userRegion && region !== userRegion) {
        // Force sign out
        signOut();

        // Show detailed error message
        openErrorDialog({
          title: formatMessage({ id: 'authRegionErrorTitle' }),
          message: formatMessage({ id: 'authRegionErrorDetails' }),
        });
      }
    }
  }, [enableAccountRegionVerification, formatMessage, openErrorDialog, region, signOut, user]);

  const loading = authLoading || userLoading;

  // use cognito to determind if user is authed or not. Prevent showing un-auth view to auth user.
  const authContext = useMemoAll<AuthInterface>({
    // Computed values
    loading,
    useUpdateMeMutationLoading,
    originLocation,
    otpAuthError,
    user: user as UserDetails,
    // Functions
    getCurrentSession,
    refetchCurrentUser,
    refreshCurrentUser,
    refreshCurrentUserWithNewSession,
    isAuthenticated,
    signIn,
    signOut,
    signUp,
    socialLogin,
    updateUserInfo,
    updateUserCommPrefs,
    updateUserFavStores,
    validateLogin,
    validateLoginOtp,
    setOriginLocation: setOriginLoc as (args: null | string) => void,
    setOtpAuthError: setOtpAuthError as (args: null | string) => void,
    setModalAuthState,
    modalAuthIsOpen,
    modalAuthState,
    isReAuthenticating,
    setIsReAuthenticating,
  });

  return (
    <AuthContext.Provider value={authContext}>
      {children}
      <ErrorDialog />
    </AuthContext.Provider>
  );
};

export default AuthContext.Consumer;
